/**
 * 📄 File Extension Support
 *
 * This module only supports `.envx` files as the standard configuration format.
 * Other extensions like `.tenv`, `.envt` are not supported.
 * Please ensure all environment files use the `.envx` extension.
 */
import path from "path";

export type FileExtsType = "envx";
export const fileExts: FileExtsType[] = ["envx"];

/**
 * Checks if the provided file path or extension matches supported environment file patterns.
 * Supported patterns include:
 *  - Exact ".envx" filename
 *  - Files starting with ".envx." (e.g., ".envx.local", ".envx.dev")
 *  - Files ending with ".envx" extension (e.g., "config.envx")
 *  - Files with the ".envx" extension recognized by the system
 *
 * @param filePathOrExt - The filename or path to validate.
 * @returns True if the file matches supported patterns; otherwise, false.
 */
export const checkFileExt = (filePathOrExt: string): boolean => {
  const baseName = path.basename(filePathOrExt);

  if (baseName === ".envx") return true;
  if (baseName.startsWith(".envx.")) return true;
  if (baseName.endsWith(".envx")) return true;

  const ext = path.extname(baseName).replace(/^\./, "");
  if (fileExts.includes(ext as FileExtsType)) return true;

  return false;
};

/**
 * Validates the file extension and throws an error if the file is not supported.
 * Only files matching the supported patterns (e.g., ".envx", ".envx.local", "config.envx") are allowed.
 *
 * @param filePath - The filename or path to validate.
 * @throws Will throw an error if the file extension is unsupported.
 */
export function throwIfUnknownExtUsed(filePath: string): void {
  if (!checkFileExt(filePath)) {
    const ext = path.extname(filePath).replace(/^\./, "");
    throw new Error(
      `[Typenv Error] Unsupported file extension '.${ext}'. Please use a '.envx' file or a valid variant such as '.envx.local'.`
    );
  }
}
